import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GPX_FEATURES } from '@rsos/gainsight';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { ChatContextProvider } from '../../CallChats/ChatContext';
import TF911ChatHeader from '../../CallChats/TF911ChatBox/TF911ChatHeader';
import { useTF911Conversation } from '../../CallChats/TF911ChatBox/useTF911Conversation';
import { trackLiveTF911CardClick } from '../../CallChats/textFrom911Trackings';
import LiveChatCard from './LiveChatCard';

const CHAT_TAB = 'chat';

/**
 * Standard TF911 chat card component that used in the ChatCard.js component.
 */
const TF911ChatCard = () => {
  const dispatch = useDispatch();

  const [showPreconfiguredMessages, setShowPreconfiguredMessages] =
    useState(false);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const displayPhoneNumber =
    formatInternationalPhoneNumberHuman(selectedCallerID);

  const {
    getOrStartConversation,
    conversationID,
    isConversationEnded,
    isOutboundSMSConversation,
  } = useTF911Conversation();

  const handleSetChatTab = useCallback(
    () => dispatch(setSelectedTab(CHAT_TAB)),
    [dispatch],
  );

  const handleLiveChatCardClick = useCallback(() => {
    handleSetChatTab();
    trackLiveTF911CardClick({
      feature: isOutboundSMSConversation
        ? GPX_FEATURES.TF911_OUTBOUND
        : GPX_FEATURES.TF911,
      conversationId: conversationID,
    });
  }, [conversationID, handleSetChatTab, isOutboundSMSConversation]);

  const handleClickShowAccordion = useCallback(() => {
    setShowPreconfiguredMessages(prev => !prev);
  }, []);

  const handleStartConversation = useCallback(
    async message => {
      const result = await getOrStartConversation(message);
      if (result.isSuccess) {
        setShowPreconfiguredMessages(false);
        handleSetChatTab();
      }
      return result;
    },
    [getOrStartConversation, handleSetChatTab],
  );

  const showStartChat = useMemo(
    () => !conversationID || isConversationEnded,
    [conversationID, isConversationEnded],
  );

  return (
    <div data-name="tf911-chat-card">
      {showStartChat ? (
        <ChatContextProvider chatType="textFrom911">
          <TF911ChatHeader
            isForOverview
            onClickShowAccordion={handleClickShowAccordion}
            onStartConversation={handleStartConversation}
            showPreconfiguredMessages={showPreconfiguredMessages}
            showStartChat={showStartChat}
            gpxFeature={
              isOutboundSMSConversation
                ? GPX_FEATURES.TF911_OUTBOUND
                : GPX_FEATURES.TF911
            }
          />
        </ChatContextProvider>
      ) : (
        <LiveChatCard
          callerID={displayPhoneNumber}
          onClick={handleLiveChatCardClick}
        />
      )}
    </div>
  );
};

export default TF911ChatCard;
